import React from "react";
import clsx from "clsx";

export type LandingGlyphName =
  | "leaf"
  | "paw"
  | "laptop"
  | "heart"
  | "bag"
  | "balloon"
  | "book"
  | "car"
  | "shield"
  | "clock"
  | "chat"
  | "card"
  | "grid";

const glyphArtwork: Record<LandingGlyphName, React.ReactNode> = {
  leaf: (
    <>
      <path d="M12.5 20.5c-.6-6.7 2.4-12.2 8.4-14.7.6 8.6-3.4 14.2-8.4 14.7Z" />
      <path d="M12.5 20.5C10.1 16 6.1 13.4 3 13c.5 4.6 4.5 7.6 9.5 7.5Z" />
    </>
  ),
  paw: (
    <>
      <ellipse cx="12" cy="15.4" rx="4.4" ry="3.6" fill="currentColor" stroke="none" />
      <circle cx="5.9" cy="10.4" r="1.9" fill="currentColor" stroke="none" />
      <circle cx="9.7" cy="7.2" r="1.9" fill="currentColor" stroke="none" />
      <circle cx="14.3" cy="7.2" r="1.9" fill="currentColor" stroke="none" />
      <circle cx="18.1" cy="10.4" r="1.9" fill="currentColor" stroke="none" />
    </>
  ),
  laptop: (
    <>
      <rect x="4.5" y="5" width="15" height="10" rx="1.6" />
      <path d="M2.5 18h19" />
      <path d="M10 15h4" />
    </>
  ),
  heart: <path d="M12 20s-7.2-4.5-7.2-9.3A4.1 4.1 0 0 1 12 8.1a4.1 4.1 0 0 1 7.2 2.6C19.2 15.5 12 20 12 20Z" />,
  bag: (
    <>
      <path d="M6 8.5h12l1.1 11.5H4.9L6 8.5Z" />
      <path d="M9.2 8.5V6.4a2.8 2.8 0 0 1 5.6 0v2.1" />
    </>
  ),
  balloon: (
    <>
      <path d="M12 3.6c2.9 0 5.2 2.4 5.2 5.4S14.9 15 12 15 6.8 12 6.8 9s2.3-5.4 5.2-5.4Z" />
      <path d="M12 15v1.8" />
      <path d="M12 16.8c-1.7.5-2.2 1.7-1.4 3.6" />
    </>
  ),
  book: (
    <>
      <path d="M12 7.3A3.4 3.4 0 0 0 9.1 5.6H4v11.6h5.4A3 3 0 0 1 12 18.8" />
      <path d="M12 7.3a3.4 3.4 0 0 1 2.9-1.7H20v11.6h-5.4A3 3 0 0 0 12 18.8" />
      <path d="M12 7.3v11.5" />
    </>
  ),
  car: (
    <>
      <path d="M4 16.2v-2.3l1.7-4.1A2 2 0 0 1 7.6 8.5h8.8a2 2 0 0 1 1.9 1.3l1.7 4.1v2.3" />
      <path d="M4 16.2h16" />
      <circle cx="7.8" cy="17.6" r="1.6" />
      <circle cx="16.2" cy="17.6" r="1.6" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.6 19 6v5.6c0 4.2-2.9 7.3-7 8.8-4.1-1.5-7-4.6-7-8.8V6l7-2.4Z" />
      <path d="m9 11.8 2.2 2.2 4-4.2" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.6V12l3.2 2" />
    </>
  ),
  chat: (
    <>
      <path d="M4.5 6.4h15v9.2h-9L5.6 19v-3.4H4.5V6.4Z" />
      <path d="M8.4 10.8h7.2" />
    </>
  ),
  card: (
    <>
      <rect x="3.5" y="6" width="17" height="12" rx="2" />
      <path d="M3.5 10.2h17" />
      <path d="M7 14.4h3.4" />
    </>
  ),
  grid: (
    <>
      <rect x="4" y="4" width="6.4" height="6.4" rx="1.4" />
      <rect x="13.6" y="4" width="6.4" height="6.4" rx="1.4" />
      <rect x="4" y="13.6" width="6.4" height="6.4" rx="1.4" />
      <rect x="13.6" y="13.6" width="6.4" height="6.4" rx="1.4" />
    </>
  ),
};

export type LandingGlyphProps = Omit<React.ComponentPropsWithoutRef<"svg">, "children"> & {
  name: LandingGlyphName;
};

const LandingGlyph: React.FC<LandingGlyphProps> = ({ name, className, ...props }) => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={clsx("h-6 w-6", className)}
      {...props}
    >
      {glyphArtwork[name]}
    </svg>
  );
};

export default LandingGlyph;
